// src/admin/PlatformSettings.js
import React, { useState } from 'react';
import {
  Save,
  Server,
  Database,
  Shield,
  Mail,
  Lock,
  Globe,
  RefreshCw,
} from 'lucide-react';

const PlatformSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'TrackerSheets',
    supportEmail: 'support@trackersheets.com',
    salesEmail: 'sales@trackersheets.com',
    defaultPlan: 'starter',
    trialDays: 14,
    maintenanceMode: false,
  });

  // API settings state
  const [apiSettings, setApiSettings] = useState({
    googleSheetsApiKey: '••••••••••••••••••••••••••••••',
    googleSheetsEnabled: true,
    stripeApiKey: '••••••••••••••••••••••••••••••',
    stripeEnabled: true,
    sendgridApiKey: '••••••••••••••••••••••••••••••',
    sendgridEnabled: true,
    maxApiCalls: 1000,
    rateLimit: 60,
  });

  // Security settings state
  const [securitySettings, setSecuritySettings] = useState({
    passwordMinLength: 8,
    passwordRequireSpecial: true,
    passwordRequireNumber: true,
    jwtExpiryHours: 24,
    adminJwtExpiryHours: 8,
    twoFactorAuthRequired: false,
    ipRestriction: false,
    allowedIps: '',
  });

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    welcomeEmailEnabled: true,
    trialEndingReminderDays: 3,
    trialEndedEmailEnabled: true,
    paymentFailedEmailEnabled: true,
    monthlyReportEmailEnabled: true,
    emailSenderName: 'TrackerSheets Support',
    emailFooterText:
      'TrackerSheets - Client Status Tracker - https://trackersheets.com',
  });

  const handleInputChange = (section, field, value) => {
    switch (section) {
      case 'general':
        setGeneralSettings({
          ...generalSettings,
          [field]: value,
        });
        break;
      case 'api':
        setApiSettings({
          ...apiSettings,
          [field]: value,
        });
        break;
      case 'security':
        setSecuritySettings({
          ...securitySettings,
          [field]: value,
        });
        break;
      case 'email':
        setEmailSettings({
          ...emailSettings,
          [field]: value,
        });
        break;
      default:
        break;
    }
  };

  const handleToggleChange = (section, field) => {
    const currentValue =
      section === 'general'
        ? generalSettings[field]
        : section === 'api'
        ? apiSettings[field]
        : section === 'security'
        ? securitySettings[field]
        : emailSettings[field];

    handleInputChange(section, field, !currentValue);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // In a real app, this would be an API call to save settings
      // const response = await fetch('/.netlify/functions/save-platform-settings', {
      //   method: 'POST',
      //   body: JSON.stringify({
      //     general: generalSettings,
      //     api: apiSettings,
      //     security: securitySettings,
      //     email: emailSettings
      //   })
      // });

      // Simulate API call with delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSaveMessage({
        type: 'success',
        text: 'Settings saved successfully',
      });

      // Clear message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({
        type: 'error',
        text: 'Error saving settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Platform Settings
          </h1>
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </button>
        </div>

        {saveMessage && (
          <div
            className={`mb-6 p-4 rounded-md ${
              saveMessage.type === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* Settings Tabs */}
          <div className="bg-gray-50 px-4 py-4 sm:px-6 border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center ${
                  activeTab === 'general'
                    ? 'bg-white text-indigo-700 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('general')}
              >
                <Globe className="mr-2 h-4 w-4" />
                General
              </button>
              <button
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center ${
                  activeTab === 'api'
                    ? 'bg-white text-indigo-700 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('api')}
              >
                <Server className="mr-2 h-4 w-4" />
                API & Integrations
              </button>
              <button
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center ${
                  activeTab === 'security'
                    ? 'bg-white text-indigo-700 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('security')}
              >
                <Shield className="mr-2 h-4 w-4" />
                Security
              </button>
              <button
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center ${
                  activeTab === 'email'
                    ? 'bg-white text-indigo-700 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('email')}
              >
                <Mail className="mr-2 h-4 w-4" />
                Email Templates
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900 border-b pb-2">
                  General Platform Settings
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Platform Name
                    </label>
                    <input
                      type="text"
                      value={generalSettings.platformName}
                      onChange={(e) =>
                        handleInputChange(
                          'general',
                          'platformName',
                          e.target.value
                        )
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={generalSettings.supportEmail}
                      onChange={(e) =>
                        handleInputChange(
                          'general',
                          'supportEmail',
                          e.target.value
                        )
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sales Email
                    </label>
                    <input
                      type="email"
                      value={generalSettings.salesEmail}
                      onChange={(e) =>
                        handleInputChange(
                          'general',
                          'salesEmail',
                          e.target.value
                        )
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Plan for New Signups
                    </label>
                    <select
                      value={generalSettings.defaultPlan}
                      onChange={(e) =>
                        handleInputChange(
                          'general',
                          'defaultPlan',
                          e.target.value
                        )
                      }
                      className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="business">Business</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trial Period (Days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="90"
                      value={generalSettings.trialDays}
                      onChange={(e) =>
                        handleInputChange(
                          'general',
                          'trialDays',
                          parseInt(e.target.value)
                        )
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center">
                    <input
                      id="maintenance-mode"
                      name="maintenance-mode"
                      type="checkbox"
                      checked={generalSettings.maintenanceMode}
                      onChange={() =>
                        handleToggleChange('general', 'maintenanceMode')
                      }
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="maintenance-mode"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Maintenance Mode (All users except admins will see
                      maintenance page)
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Enable this during system updates or when troubleshooting
                    critical issues.
                  </p>
                </div>
              </div>
            )}

            {/* API Settings */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900 border-b pb-2">
                  API & Integration Settings
                </h2>

                <div className="mt-4 space-y-6">
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center">
                      <Database className="w-4 h-4 mr-1" /> Google Sheets API
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <input
                          type="password"
                          value={apiSettings.googleSheetsApiKey}
                          onChange={(e) =>
                            handleInputChange(
                              'api',
                              'googleSheetsApiKey',
                              e.target.value
                            )
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="google-sheets-enabled"
                          name="google-sheets-enabled"
                          type="checkbox"
                          checked={apiSettings.googleSheetsEnabled}
                          onChange={() =>
                            handleToggleChange('api', 'googleSheetsEnabled')
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="google-sheets-enabled"
                          className="ml-2 block text-sm text-gray-900"
                        >
                          Enable Google Sheets Integration
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center">
                      <Database className="w-4 h-4 mr-1" /> Stripe Payment
                      Integration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stripe API Key
                        </label>
                        <input
                          type="password"
                          value={apiSettings.stripeApiKey}
                          onChange={(e) =>
                            handleInputChange(
                              'api',
                              'stripeApiKey',
                              e.target.value
                            )
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="stripe-enabled"
                          name="stripe-enabled"
                          type="checkbox"
                          checked={apiSettings.stripeEnabled}
                          onChange={() =>
                            handleToggleChange('api', 'stripeEnabled')
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="stripe-enabled"
                          className="ml-2 block text-sm text-gray-900"
                        >
                          Enable Stripe Payments
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-md font-medium text-gray-900 mb-2 flex items-center">
                      <Mail className="w-4 h-4 mr-1" /> SendGrid Email
                      Integration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          SendGrid API Key
                        </label>
                        <input
                          type="password"
                          value={apiSettings.sendgridApiKey}
                          onChange={(e) =>
                            handleInputChange(
                              'api',
                              'sendgridApiKey',
                              e.target.value
                            )
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="sendgrid-enabled"
                          name="sendgrid-enabled"
                          type="checkbox"
                          checked={apiSettings.sendgridEnabled}
                          onChange={() =>
                            handleToggleChange('api', 'sendgridEnabled')
                          }
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="sendgrid-enabled"
                          className="ml-2 block text-sm text-gray-900"
                        >
                          Enable SendGrid Emails
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-md font-medium text-gray-900 mb-2">
                      API Rate Limiting
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max API Calls Per Day (Per Business)
                        </label>
                        <input
                          type="number"
                          min="100"
                          max="10000"
                          value={apiSettings.maxApiCalls}
                          onChange={(e) =>
                            handleInputChange(
                              'api',
                              'maxApiCalls',
                              parseInt(e.target.value)
                            )
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rate Limit (Requests Per Minute)
                        </label>
                        <input
                          type="number"
                          min="10"
                          max="300"
                          value={apiSettings.rateLimit}
                          onChange={(e) =>
                            handleInputChange(
                              'api',
                              'rateLimit',
                              parseInt(e.target.value)
                            )
                          }
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900 border-b pb-2">
                  Security Settings
                </h2>

                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                    <Lock className="w-4 h-4 mr-1" /> Password Policy
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Password Length
                      </label>
                      <input
                        type="number"
                        min="6"
                        max="32"
                        value={securitySettings.passwordMinLength}
                        onChange={(e) =>
                          handleInputChange(
                            'security',
                            'passwordMinLength',
                            parseInt(e.target.value)
                          )
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        id="password-require-special"
                        name="password-require-special"
                        type="checkbox"
                        checked={securitySettings.passwordRequireSpecial}
                        onChange={() =>
                          handleToggleChange(
                            'security',
                            'passwordRequireSpecial'
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="password-require-special"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Require Special Characters
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="password-require-number"
                        name="password-require-number"
                        type="checkbox"
                        checked={securitySettings.passwordRequireNumber}
                        onChange={() =>
                          handleToggleChange(
                            'security',
                            'passwordRequireNumber'
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="password-require-number"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Require Numbers
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                    <Shield className="w-4 h-4 mr-1" /> Authentication
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        JWT Expiry (Hours) - Business Users
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="168" // 1 week
                        value={securitySettings.jwtExpiryHours}
                        onChange={(e) =>
                          handleInputChange(
                            'security',
                            'jwtExpiryHours',
                            parseInt(e.target.value)
                          )
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        JWT Expiry (Hours) - Admin Users
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24" // 1 day
                        value={securitySettings.adminJwtExpiryHours}
                        onChange={(e) =>
                          handleInputChange(
                            'security',
                            'adminJwtExpiryHours',
                            parseInt(e.target.value)
                          )
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        id="two-factor-auth"
                        name="two-factor-auth"
                        type="checkbox"
                        checked={securitySettings.twoFactorAuthRequired}
                        onChange={() =>
                          handleToggleChange(
                            'security',
                            'twoFactorAuthRequired'
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="two-factor-auth"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Require Two-Factor Authentication for Admins
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-md font-medium text-gray-900 mb-3">
                    IP Restrictions
                  </h3>
                  <div className="flex items-center mb-3">
                    <input
                      id="ip-restriction"
                      name="ip-restriction"
                      type="checkbox"
                      checked={securitySettings.ipRestriction}
                      onChange={() =>
                        handleToggleChange('security', 'ipRestriction')
                      }
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="ip-restriction"
                      className="ml-2 block text-sm text-gray-900"
                    >
                      Enable IP Restriction for Admin Panel
                    </label>
                  </div>
                  {securitySettings.ipRestriction && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Allowed IPs (comma separated)
                      </label>
                      <input
                        type="text"
                        value={securitySettings.allowedIps}
                        onChange={(e) =>
                          handleInputChange(
                            'security',
                            'allowedIps',
                            e.target.value
                          )
                        }
                        placeholder="192.168.1.1, 10.0.0.5"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Be careful with IP restrictions. Enter your current IP
                        address to avoid being locked out.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Email Templates */}
            {activeTab === 'email' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900 border-b pb-2">
                  Email Templates & Notifications
                </h2>

                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-3">
                    Email Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Sender Name
                      </label>
                      <input
                        type="text"
                        value={emailSettings.emailSenderName}
                        onChange={(e) =>
                          handleInputChange(
                            'email',
                            'emailSenderName',
                            e.target.value
                          )
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Footer Text
                      </label>
                      <input
                        type="text"
                        value={emailSettings.emailFooterText}
                        onChange={(e) =>
                          handleInputChange(
                            'email',
                            'emailFooterText',
                            e.target.value
                          )
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-md font-medium text-gray-900 mb-3">
                    Email Notifications
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="welcome-email"
                        name="welcome-email"
                        type="checkbox"
                        checked={emailSettings.welcomeEmailEnabled}
                        onChange={() =>
                          handleToggleChange('email', 'welcomeEmailEnabled')
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="welcome-email"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Send welcome email to new businesses
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="trial-ending-email"
                        name="trial-ending-email"
                        type="checkbox"
                        checked={emailSettings.trialEndingReminderDays > 0}
                        onChange={() =>
                          handleInputChange(
                            'email',
                            'trialEndingReminderDays',
                            emailSettings.trialEndingReminderDays > 0 ? 0 : 3
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="trial-ending-email"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Send trial ending reminder email
                      </label>

                      {emailSettings.trialEndingReminderDays > 0 && (
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={emailSettings.trialEndingReminderDays}
                          onChange={(e) =>
                            handleInputChange(
                              'email',
                              'trialEndingReminderDays',
                              parseInt(e.target.value)
                            )
                          }
                          className="ml-2 w-16 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      )}

                      {emailSettings.trialEndingReminderDays > 0 && (
                        <span className="ml-1 text-sm text-gray-500">
                          days before expiry
                        </span>
                      )}
                    </div>

                    <div className="flex items-center">
                      <input
                        id="trial-ended-email"
                        name="trial-ended-email"
                        type="checkbox"
                        checked={emailSettings.trialEndedEmailEnabled}
                        onChange={() =>
                          handleToggleChange('email', 'trialEndedEmailEnabled')
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="trial-ended-email"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Send trial ended email
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="payment-failed-email"
                        name="payment-failed-email"
                        type="checkbox"
                        checked={emailSettings.paymentFailedEmailEnabled}
                        onChange={() =>
                          handleToggleChange(
                            'email',
                            'paymentFailedEmailEnabled'
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="payment-failed-email"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Send payment failed email
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="monthly-report-email"
                        name="monthly-report-email"
                        type="checkbox"
                        checked={emailSettings.monthlyReportEmailEnabled}
                        onChange={() =>
                          handleToggleChange(
                            'email',
                            'monthlyReportEmailEnabled'
                          )
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="monthly-report-email"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Send monthly usage report email
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-md font-medium text-gray-900 mb-3">
                    Template Editor
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Select an email template to edit its content, subject line,
                    and appearance.
                  </p>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-center text-gray-500">
                      Email template editor is under development. Coming soon.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
